import { Check, Sparkles } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { CONTACT } from "@/lib/contacts";
import ProfitEstimator from "@/components/landing/ProfitEstimator";

const BADGE_STYLES = {
  brand: "bg-[var(--brand)] text-black",
  teal: "bg-[#43B581] text-black",
  yellow: "bg-[var(--accent2)] text-black",
};

const Pricing = () => {
  const { t } = useLanguage();

  return (
    <section
      id="pricing"
      aria-labelledby="pricing-heading"
      data-testid="pricing-section"
      className="relative py-20 md:py-32"
    >
      <div className="max-w-7xl mx-auto px-5 md:px-8">
        <header className="text-center max-w-2xl mx-auto">
          <p className="text-xs md:text-sm font-semibold uppercase tracking-[0.25em] text-[var(--brand)]">
            {t.pricing.kicker}
          </p>
          <h2
            id="pricing-heading"
            className="mt-3 font-heading text-3xl sm:text-4xl lg:text-5xl font-black tracking-tighter text-white"
          >
            {t.pricing.title}
          </h2>
          <p className="mt-4 text-zinc-200 text-base md:text-lg">
            {t.pricing.subtitle}
          </p>
        </header>

        <ul className="mt-12 md:mt-16 grid grid-cols-1 lg:grid-cols-3 gap-5 md:gap-6">
          {t.pricing.packages.map((pkg, i) => (
            <li key={i} data-testid={`pricing-card-${i}`}>
              <article
                aria-labelledby={`pricing-pkg-${i}`}
                className={`relative h-full rounded-2xl border p-7 md:p-8 flex flex-col ${
                  pkg.popular
                    ? "border-[rgb(var(--brand-rgb)/0.6)] bg-[rgb(var(--brand-rgb)/0.04)] glow-ring"
                    : "border-white/10 bg-white/[0.03]"
                }`}
              >
                <p
                  className={`absolute -top-3 left-1/2 -translate-x-1/2 inline-flex items-center gap-1 px-3 h-6 rounded-full text-[10px] font-bold uppercase tracking-[0.2em] whitespace-nowrap ${
                    BADGE_STYLES[pkg.accent] || BADGE_STYLES.brand
                  }`}
                >
                  <Sparkles className="h-3 w-3" aria-hidden="true" />
                  {pkg.badge}
                </p>

                <h3
                  id={`pricing-pkg-${i}`}
                  className="font-heading text-xl md:text-2xl font-bold text-white tracking-tight"
                >
                  {pkg.name}
                </h3>
                <p className="mt-5 flex items-baseline gap-2">
                  <span className="font-heading text-5xl md:text-6xl font-black tracking-tighter text-white">
                    {pkg.price}
                  </span>
                  <span className="text-base font-semibold text-zinc-200">USDT</span>
                </p>
                <p className="mt-1 text-xs text-zinc-300">
                  {t.pricing.one_time} · {pkg.note}
                </p>

                <ul className="mt-6 space-y-2.5 flex-1">
                  {pkg.features.map((f, j) => (
                    <li key={j} className="flex items-start gap-2.5 text-sm text-zinc-300">
                      <Check
                        className="h-4 w-4 mt-0.5 shrink-0 text-[var(--brand)]"
                        aria-hidden="true"
                      />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>

                <a
                  href={CONTACT.registration}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`pricing-cta-${i}`}
                  className={`mt-7 inline-flex items-center justify-center h-12 rounded-full font-bold text-sm transition-colors ${
                    pkg.popular
                      ? "bg-[var(--brand)] text-black hover:bg-[var(--brand-hover)]"
                      : "border border-white/15 text-white hover:bg-white hover:text-black"
                  }`}
                >
                  {t.pricing.cta}
                  <span className="sr-only">
                    {" "}
                    — {pkg.name} {t.a11y.external}
                  </span>
                </a>
              </article>
            </li>
          ))}
        </ul>

        <ProfitEstimator />
      </div>
    </section>
  );
};

export default Pricing;
